<template>
    <div class="videos-page">
        <!-- Page Header -->
        <section class="page-header">
            <div class="container">
                <nav class="breadcrumbs">
                    <router-link to="/">Inicio</router-link>
                    <span class="separator">/</span>
                    <span class="current">Videos</span>
                </nav>
                <h1 class="page-title">Videos Tutoriales</h1>
                <p class="page-subtitle">
                    Aprende a usar nuestros productos con nuestros videos paso a paso
                </p>
            </div>
        </section>

        <!-- Content -->
        <section class="videos-content">
            <div class="container">
                <!-- Search Box -->
                <div class="search-box">
                    <i class="fas fa-search"></i>
                    <input 
                        type="text" 
                        v-model="searchQuery"
                        @input="handleSearch"
                        placeholder="Buscar videos..."
                    >
                </div>
                
                <!-- Loading inicial -->
                <div v-if="initialLoading" class="loading-state">
                    <div class="spinner"></div>
                    <p>Cargando videos...</p>
                </div>
                
                <!-- Empty State -->
                <div v-else-if="displayedVideos.length === 0" class="empty-state">
                    <i class="fas fa-video"></i>
                    <h3>No se encontraron videos</h3>
                    <p v-if="searchQuery">Intenta con otros términos de búsqueda</p>
                    <p v-else>No hay videos disponibles en este momento</p>
                </div>
                
                <!-- Videos Grid -->
                <div v-else class="videos-grid">
                    <div 
                        v-for="video in displayedVideos" 
                        :key="video.id"
                        class="video-card"
                        @click="openVideo(video)"
                    >
                        <div class="video-thumbnail">
                            <img 
                                :src="getVideoThumbnail(video)" 
                                :alt="video.titulo"
                                @error="handleImageError"
                            >
                            <div class="play-overlay">
                                <i class="fas fa-play"></i>
                            </div>
                            <div v-if="video.proveedor" class="video-badge">
                                {{ video.proveedor }}
                            </div>
                        </div>
                        
                        <div class="video-info">
                            <h3 class="video-title">{{ video.titulo }}</h3>
                        </div>
                    </div>
                </div>

                <!-- Loading More -->
                <div v-if="loadingMore" class="loading-more">
                    <div class="spinner-small"></div>
                    <p>Cargando más videos...</p>
                </div>

                <!-- Scroll Trigger -->
                <div ref="scrollTrigger" class="scroll-trigger"></div>
            </div>
        </section>

        <!-- Video Modal -->
        <transition name="modal">
            <div v-if="selectedVideo" class="modal-overlay" @click="closeVideo">
                <div class="modal-container" @click.stop>
                    <button class="modal-close" @click="closeVideo">
                        <i class="fas fa-times"></i>
                    </button>
                    
                    <div class="video-player" v-html="getEmbedCode(selectedVideo)"></div>
                    
                    <div class="modal-info">
                        <h2>{{ selectedVideo.titulo }}</h2>
                        <div class="modal-meta">
                            <span v-if="selectedVideo.proveedor" class="meta-badge">
                                {{ selectedVideo.proveedor }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import api from '../services/api';

export default {
    name: 'Videos',
    data() {
        return {
            allVideos: [],
            displayedVideos: [],
            searchQuery: '',
            selectedVideo: null,
            initialLoading: true,
            loadingMore: false,
            currentPage: 1,
            perPage: 12,
            hasMore: true,
            observer: null
        };
    },
    async created() {
        await this.loadVideos();
        this.displayVideos();
    },
    mounted() {
        this.setupInfiniteScroll();
    },
    beforeDestroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    },
    methods: {
        async loadVideos() {
            try {
                this.initialLoading = true;
                const response = await api.getVideos();
                this.allVideos = response.data || [];
                this.initialLoading = false;
            } catch (error) {
                console.error('Error loading videos:', error);
                this.initialLoading = false;
            }
        },
        displayVideos() {
            const start = 0;
            const end = this.currentPage * this.perPage;
            
            if (this.searchQuery) {
                const filtered = this.filterVideos();
                this.displayedVideos = filtered.slice(start, end);
                this.hasMore = end < filtered.length;
            } else {
                this.displayedVideos = this.allVideos.slice(start, end);
                this.hasMore = end < this.allVideos.length;
            }
        },
        filterVideos() {
            const query = this.searchQuery.toLowerCase();
            return this.allVideos.filter(video => 
                video.titulo.toLowerCase().includes(query)
            );
        },
        loadMore() {
            if (!this.hasMore || this.loadingMore) return;
            
            this.loadingMore = true;
            
            setTimeout(() => {
                this.currentPage++;
                this.displayVideos();
                this.loadingMore = false;
            }, 500);
        },
        handleSearch() {
            this.currentPage = 1;
            this.displayVideos();
        },
        setupInfiniteScroll() {
            const options = {
                root: null,
                rootMargin: '200px',
                threshold: 0
            };
            
            this.observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && this.hasMore && !this.loadingMore) {
                        this.loadMore();
                    }
                });
            }, options);
            
            if (this.$refs.scrollTrigger) {
                this.observer.observe(this.$refs.scrollTrigger);
            }
        },
        getVideoThumbnail(video) {
            // Si tiene thumbnail personalizado
            if (video.thumbnail) {
                if (video.thumbnail.startsWith('http')) {
                    return video.thumbnail;
                }
                return `/storage/${video.thumbnail}`;
            }
            
            // Generar thumbnail según proveedor
            if (video.proveedor && video.video_id) {
                switch (video.proveedor.toLowerCase()) {
                    case 'youtube':
                        return `https://img.youtube.com/vi/${video.video_id}/maxresdefault.jpg`;
                    case 'vimeo':
                        return `https://vumbnail.com/${video.video_id}.jpg`;
                    case 'dailymotion':
                        return `https://www.dailymotion.com/thumbnail/video/${video.video_id}`;
                }
            }
            
            // Placeholder
            return 'https://via.placeholder.com/640x360/1e3a5f/d4af37?text=Video';
        },
        getEmbedCode(video) {
            // Si tiene código embed personalizado
            if (video.video_embed) {
                return video.video_embed;
            }
            
            // Generar embed según proveedor
            if (video.proveedor && video.video_id) {
                switch (video.proveedor.toLowerCase()) {
                    case 'youtube':
                        return `<iframe width="100%" height="100%" src="https://www.youtube.com/embed/${video.video_id}?autoplay=1" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
                    case 'vimeo':
                        return `<iframe src="https://player.vimeo.com/video/${video.video_id}?autoplay=1" width="100%" height="100%" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;
                    case 'dailymotion':
                        return `<iframe frameborder="0" width="100%" height="100%" src="https://www.dailymotion.com/embed/video/${video.video_id}?autoplay=1" allowfullscreen allow="autoplay"></iframe>`;
                }
            }
            
            return '<p>No se puede reproducir este video</p>';
        },
        handleImageError(e) {
            e.target.src = 'https://via.placeholder.com/640x360/1e3a5f/d4af37?text=Video';
        },
        openVideo(video) {
            this.selectedVideo = video;
            document.body.style.overflow = 'hidden';
        },
        closeVideo() {
            this.selectedVideo = null;
            document.body.style.overflow = '';
        }
    }
};
</script>

<style scoped>
.videos-page {
    background-color: var(--background);
    min-height: 100vh;
    padding-top: 80px;
}

/* PAGE HEADER */
.page-header {
    background: linear-gradient(135deg, var(--primary-dark), var(--primary-blue));
    padding: 60px 2rem;
    position: relative;
}

.page-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 50%, rgba(212, 175, 55, 0.1), transparent 60%);
}

.container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 1;
}

/* BREADCRUMBS */
.breadcrumbs {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
}

.breadcrumbs a {
    color: var(--text-gray);
    text-decoration: none;
    transition: color 0.3s ease;
}

.breadcrumbs a:hover {
    color: var(--accent-gold);
}

.breadcrumbs .separator {
    color: rgba(212, 175, 55, 0.3);
}

.breadcrumbs .current {
    color: var(--accent-gold);
    font-weight: 600;
}

.page-title {
    font-family: 'Playfair Display', serif;
    font-size: 3rem;
    margin-bottom: 1rem;
    color: var(--text-light);
}

.page-subtitle {
    font-size: 1.2rem;
    color: var(--text-gray);
}

/* CONTENT */
.videos-content {
    padding: 60px 2rem;
}

/* SEARCH BOX */
.search-box {
    position: relative;
    max-width: 600px;
    margin: 0 auto 4rem;
}

.search-box i {
    position: absolute;
    left: 1.5rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--accent-gold);
    font-size: 1.125rem;
}

.search-box input {
    width: 100%;
    padding: 1rem 1.5rem 1rem 3.5rem;
    background: var(--card-bg);
    border: 1px solid rgba(212, 175, 55, 0.2);
    border-radius: 50px;
    color: var(--text-light);
    font-size: 1rem;
    transition: all 0.3s ease;
}

.search-box input:focus {
    outline: none;
    border-color: var(--accent-gold);
    box-shadow: 0 0 0 4px rgba(212, 175, 55, 0.1);
}

.search-box input::placeholder {
    color: var(--text-gray);
}

/* VIDEOS GRID */
.videos-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 2rem;
    margin-bottom: 3rem;
}

.video-card {
    background: var(--card-bg);
    border-radius: 16px;
    overflow: hidden;
    border: 1px solid rgba(212, 175, 55, 0.1);
    transition: all 0.4s ease;
    cursor: pointer;
}

.video-card:hover {
    transform: translateY(-10px);
    border-color: var(--accent-gold);
    box-shadow: 0 20px 60px rgba(212, 175, 55, 0.2);
}

.video-thumbnail {
    position: relative;
    padding-bottom: 56.25%;
    background: var(--primary-dark);
    overflow: hidden;
}

.video-thumbnail img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
}

.video-card:hover .video-thumbnail img {
    transform: scale(1.1);
}

.play-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 70px;
    height: 70px;
    background: linear-gradient(135deg, var(--accent-gold), var(--accent-teal));
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-dark);
    font-size: 1.5rem;
    padding-left: 5px;
    transition: all 0.3s ease;
    z-index: 1;
}

.video-card:hover .play-overlay {
    transform: translate(-50%, -50%) scale(1.2);
    box-shadow: 0 10px 30px rgba(212, 175, 55, 0.4);
}

.video-badge {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: rgba(212, 175, 55, 0.9);
    color: var(--primary-dark);
    padding: 0.4rem 0.8rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: capitalize;
    z-index: 1;
}

.video-info {
    padding: 1.5rem;
}

.video-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-light);
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin: 0;
}

/* LOADING STATES */
.loading-state,
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 5rem 2rem;
    text-align: center;
}

.spinner {
    width: 50px;
    height: 50px;
    border: 5px solid rgba(212, 175, 55, 0.1);
    border-top-color: var(--accent-gold);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
}

.loading-state p {
    color: var(--text-gray);
}

.empty-state i {
    font-size: 4rem;
    color: rgba(212, 175, 55, 0.3);
    margin-bottom: 1rem;
}

.empty-state h3 {
    font-family: 'Playfair Display', serif;
    font-size: 1.8rem;
    color: var(--text-light);
    margin-bottom: 0.5rem;
}

.empty-state p {
    color: var(--text-gray);
}

/* LOADING MORE */
.loading-more {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 2rem;
    color: var(--text-gray);
}

.spinner-small {
    width: 30px;
    height: 30px;
    border: 3px solid rgba(212, 175, 55, 0.1);
    border-top-color: var(--accent-gold);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.scroll-trigger {
    height: 1px;
    width: 100%;
}

/* MODAL */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.95);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
}

.modal-container {
    max-width: 1200px;
    width: 100%;
    background: var(--card-bg);
    border-radius: 20px;
    overflow: hidden;
    position: relative;
    border: 1px solid rgba(212, 175, 55, 0.2);
}

.modal-close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    width: 45px;
    height: 45px;
    background: rgba(212, 175, 55, 0.9);
    border: none;
    border-radius: 50%;
    color: var(--primary-dark);
    font-size: 1.25rem;
    cursor: pointer;
    z-index: 10;
    transition: all 0.3s ease;
}

.modal-close:hover {
    background: var(--accent-gold);
    transform: scale(1.1);
}

.video-player {
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;
    background: #000;
}

.video-player >>> iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

.modal-info {
    padding: 2rem;
}

.modal-info h2 {
    font-family: 'Playfair Display', serif;
    font-size: 1.8rem;
    font-weight: 700;
    color: var(--text-light);
    margin-bottom: 1rem;
}

.modal-meta {
    display: flex;
    gap: 1rem;
}

.meta-badge {
    background: rgba(212, 175, 55, 0.1);
    color: var(--accent-gold);
    padding: 0.4rem 0.8rem;
    border-radius: 20px;
    font-size: 0.875rem;
    font-weight: 600;
    text-transform: capitalize;
}

.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

/* RESPONSIVE */
@media (max-width: 768px) {
    .page-title {
        font-size: 2rem;
    }
    
    .videos-grid {
        grid-template-columns: 1fr;
    }
    
    .modal-overlay {
        padding: 1rem;
    }
    
    .modal-info {
        padding: 1.5rem;
    }
    
    .modal-info h2 {
        font-size: 1.5rem;
    }
}
</style>